<template>
    <div class="channel-details">
        <h5><i :class="globalClass"></i> {{ resource.display_name }}</h5>
        <div v-if="monitorData.details" style="display: flex;" class="resource-container">
            <div
                class="overlay"
                v-if="loading || canTryAgain"
            >
                <div
                    class="loader-container"
                    v-if="loading"
                >
                    <div
                        class="loader"
                        v-loading="loading"
                    ></div>
                    <span class="loader-text">
                        {{actionLabels.loading}} {{nodeInfo.type === 'taker'?'taker':'resource'}}{{isVM?'\'s blanking VM':''}}
                    </span>
                </div>
                <div
                    v-if="canTryAgain"
                    class="retry-container"
                >
                    <img src="/static/icon/error-icon.png">
                    <div class="retry-title">
                        Failed to {{actionLabels.action}} the {{nodeInfo.type === 'taker'?'taker':'resource'}}{{isVM?'\'s blanking VM':''}}.
                    </div>
                    <p class="retry-text">The resource could not be {{actionLabels.verb}}. Please contact an administrator.</p>
                    <div>
                        <el-button
                            type="primary"
                            size="small"
                            class="actions-button"
                            @click="cancelRetry"
                        >
                            Ok
                        </el-button>
                    </div>
                </div>
            </div>
            <vue-pipeline
                ref="pipeline"
                :data="monitoringData"
                lineStyle="bessel"
                :showArrow="true"
                @select="handleSelect"
            ></vue-pipeline>
            <div class="node-details">
                <div v-if="nodeInfo">
                    <input-details
                        v-if="
                            nodeInfo.type === 'input'
                                && (userIsAtLeastAdmin
                                || userIsEntityAdmin(entityUid))
                        "
                        :nodeDetails="nodeInfo"
                    ></input-details>
                    <zixi-details
                        v-if="nodeInfo.type === 'zixi'"
                        :nodeDetails="nodeInfo"
                        :resource="resource"
                        :resource-is-feed="resourceIsFeed"
                        :entity-uid="entityUid"
                        @watchModelStatus="watchReconfiguration"
                    ></zixi-details>
                    <dispatch-details
                        v-if="nodeInfo.type === 'dispatch'"
                        :nodeDetails="nodeInfo"
                        :resource="resource"
                        :resource-is-feed="resourceIsFeed"
                        :entity-uid="entityUid"
                        @watchModelStatus="watchReconfiguration"
                    ></dispatch-details>
                    <delivery-details
                        v-if="nodeInfo.type === 'delivery'"
                        :nodeDetails="nodeInfo"
                        :resource="resource"
                        :resource-is-feed="resourceIsFeed"
                        :entity-uid="entityUid"
                        @watchModelStatus="watchReconfiguration"
                    ></delivery-details>
                    <taker-details
                        v-if="nodeInfo.type === 'taker' &&
                        (userIsAtLeastAdmin || this.userIsEntityAdmin(this.entityUid))"
                        :nodeDetails="nodeInfo"
                        :resource="resource"
                        :resource-is-feed="resourceIsFeed"
                        :entity-uid="entityUid"
                        @watchModelStatus="watchReconfiguration"
                    ></taker-details>
                    <preview-details
                        v-if="nodeInfo.type === 'preview' && resourceIsFeed"
                        :nodeDetails="nodeInfo">
                    </preview-details>
                </div>
                <div v-else>
                    <small>click on a node for more info</small>
                </div>
            </div>
        </div>
        <div v-else>
            No monitoring data available for this {{resourceIsFeed ? 'feed' : 'channel'}}
        </div>
        <div v-if="monitorData.modification_date !== undefined">
            Last modification: {{ modificationDelta() }} ({{ monitorData.modification_date | moment('YYYY-MM-DD HH:mm:ss')}} - Local time)
        </div>
        <div v-if="monitorData.verbose_status">{{ monitorData.verbose_status }}</div>
        <button @click="collapsed=true" v-if="!collapsed">Collapse</button>
        <hr>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import inputDetails from './inputDetails.vue';
import zixiDetails from './zixiDetails.vue';
import dispatchDetails from './dispatchDetails.vue';
import deliveryDetails from './deliveryDetails.vue';
import takerDetails from './takerDetails.vue';
import previewDetails from './previewDetails.vue';

export default {

    components: {
        inputDetails,
        deliveryDetails,
        dispatchDetails,
        zixiDetails,
        takerDetails,
        previewDetails
    },

    props: {
        resource: {
            type: Object,
            required: true
        },
        monitorData: {
            type: Object,
            required: true
        },
        resourceIsFeed: {
            type: Boolean,
            default: true
        },
        reconfigurations:{
            type: Array,
            default: ()=>{
                return [];
            },
        }
    },

    data: function(){
        return {
            loading: false,
            isWatchingStatus: false,
            reconfigurationTimeoutId: null,
            canTryAgain: false,
            actionLabels: null,
            requestUrl: '',
            resourceName: '',
            collapsed: true,
            nodeInfo: null,
            isVM:false,
            statusForPipeline: {
                'OK': 'success',
                'WARNING': 'unstable',
                'CRITICAL': 'failure'
            }

        };
    },
    watch: {
        monitorData: {
            handler (){
                if (this.nodeInfo) {
                    this.nodeInfo = this.monitoringData.find(data => data.name === this.nodeInfo.name) || null;
                }
                if (this.$refs.pipeline) {
                    this.$refs.pipeline.render();
                }
            },
            deep: true,
            // immediate: true,
        },
        reconfigurations: {
            handler(val){
                let pendingReconf = null;
                let field = this.resourceIsFeed?'feed_uid':'channel_uid';
                if(
                    val.length > 0
                    && (
                        this.userIsAtLeastAdmin
                        || this.userIsEntityAdmin(this.entityUid)
                    )
                ){
                    pendingReconf = val.find(reconf => reconf[field] === this.resource.uid);
                }
                if(pendingReconf){
                    let data = {
                        name: pendingReconf.node_name,
                        action: pendingReconf.reconfiguration_type.toLowerCase(),
                        url: pendingReconf.url,
                    };
                    this.nodeInfo = this.$refs.pipeline.nodeList.find(node => node.name === pendingReconf.node_name);

                    if(!['reconfigure'].includes(data.action) && this.nodeInfo.type === 'taker'){
                        data['isVM'] = true;
                    }

                    if(!this.isWatchingStatus){
                        this.watchReconfiguration(data);
                    }
                }
            },
            deep: true,
        }
    },
    computed: {
        ...mapGetters([
            'userIsAtLeastAdmin',
            'userIsAtLeastManager',
            'userIsEntityAdmin',
        ]),
        entityUid(){
            return this.resource.scopes.entity_uid;
        },
        resourceHasAnExternalSource() {
            return this.monitorData.details.zixi_main === null
                && this.monitorData.details.zixi_backup === null;
        },
        resourceBackUpMissing() {
            return this.monitorData.details.zixi_backup === null
                && this.monitorData.details.flow_dispatch_backup === null
                && this.monitorData.details.flow_delivery_backups === null;
        },
        inputStatus() {
            if(this.resource.status === 'FAILED' || this.monitorData.global_status === undefined || this.monitorData.global_status === 'CRITICAL'){
                return 'failure';
            } else if (this.monitorData.global_status === 'WARNING') {
                return 'unstable';
            } else {
                return 'success';
            }
        },
        inputData() {
            let nextValue = [{ index: 1, weight: 2 }];
            if (this.resourceBackUpMissing === false) {
                nextValue.push({ index: 2, weight: 2 });
            }
            return {
                name: '-IN-',
                status: this.inputStatus,
                next: nextValue,
                // conditional key creation
                ...(
                    (
                        this.userIsAtLeastAdmin
                        || this.userIsEntityAdmin(this.entityUid)
                    ) && {
                        ...this.monitorData.details.in_data,
                        type: 'input'
                    }
                )
            };
        },
        zixiDataMain() {
            if (this.monitorData.details.zixi_main === null) {
                return {
                    name: 'Test ZIXI',
                    hint: 'Test ZIXI',
                    next: [
                      { index: 3, weight: 2 },
                    ],
                };
            }
            return {
                //order is important for the 'status' key
                ...( this.userIsAtLeastManager &&{
                    ...this.monitorData.details.zixi_main,
                    type: 'zixi'
                }),
                name: 'Main ZIXI',
                hint: 'Main ZIXI',
                status: this.statusForPipeline[this.monitorData.details.zixi_main.status],
                next: [
                  { index: 3, weight: 2 },
                ],
            };
        },
        zixiDataBackup() {
            let nextValue = [];
            let statusValue = 'end';
            if (this.resourceBackUpMissing === false) {
                nextValue.push({ index: 4, weight: 2 });
                statusValue = this.statusForPipeline[this.monitorData.details.zixi_backup.status];
            }
            return {
                ...( this.userIsAtLeastManager &&{
                    ...this.monitorData.details.zixi_backup,
                    type: 'zixi'
                }),
                name: 'Backup ZIXI',
                hint: 'Backup ZIXI',
                status: statusValue,
                next: nextValue,
            };
        },
        dispatchDataMain() {
            return {
                ...( this.userIsAtLeastManager &&{
                    ...this.monitorData.details.flow_dispatch_main,
                    type: 'dispatch'
                }),
                name: 'Main Dispatch',
                hint: 'Main Dispatch',
                status: this.statusForPipeline[this.monitorData.details.flow_dispatch_main.status],
                next: [],
            };
        },
        dispatchDataBackup() {
            return {
                ...(this.userIsAtLeastManager &&{
                    ...this.monitorData.details.flow_dispatch_backup,
                    type: 'dispatch'
                }),
                name: 'Backup Dispatch',
                hint: 'Backup Dispatch',
                status: this.statusForPipeline[this.monitorData.details.flow_dispatch_backup.status],
                next: [],
            };
        },
        baseData() {
            if (Object.keys(this.monitorData).length === 0) {
                return [];
            }
            let dataToReturn = [this.inputData];

            if (this.resourceHasAnExternalSource === false) {
                dataToReturn.push(this.zixiDataMain);
                dataToReturn.push(this.zixiDataBackup);
            }
            dataToReturn.push(this.dispatchDataMain);
            if (this.resourceBackUpMissing === false) {
                dataToReturn.push(this.dispatchDataBackup);
            }
            return dataToReturn;
        },
        deliveryMainData() {
            return this.monitorData.details.flow_delivery_mains.map((delivery, index) => {
                return {
                    ...(this.userIsAtLeastManager && {
                        ...delivery,
                        taker_outputs: delivery.taker_outputs,
                        type: 'delivery',
                    }),
                    name: this.setMainDeliveryName(delivery),
                    hint: this.setMainDeliveryName(delivery),
                    status: this.statusForPipeline[delivery.status],
                    next: [],
                };
            });
        },
        deliveryBackupData() {
            return this.monitorData.details.flow_delivery_backups.map((delivery, index) => {
                return {
                    ...(this.userIsAtLeastManager &&{
                        ...delivery,
                        taker_outputs: delivery.taker_outputs,
                        type: 'delivery',
                    }),
                    name: this.setBackupDeliveryName(delivery),
                    hint: this.setBackupDeliveryName(delivery),
                    status: this.statusForPipeline[delivery.status],
                    region: delivery.region,
                    next: [],
                };
            });
        },
        previewData() {
            if(this.resourceIsFeed){
                return {
                    ...((this.userIsAtLeastAdmin || this.userIsEntityAdmin(this.entityUid))&& {
                        ...this.monitorData.details.preview,
                        type: 'preview',
                    }),
                    name: 'Preview',
                    hint: 'Preview',
                    status: this.statusForPipeline[this.monitorData.details.preview.status],
                };
            }
            return;
        },
        monitoringData() {
            if (Object.keys(this.monitorData).length === 0) {
                return [];
            }

            let data = [
                ...this.baseData,
                ...this.deliveryMainData,
            ];

            if (this.resourceBackUpMissing === false) {
                data = [...data, ...this.deliveryBackupData];
            }
            // the base data will always have 1 start entry, 2 zixi entries and 2 dispatch entries, therefore the rest of the data starts at the 6th entry which is index 5.
            // this index will be incremented after each node creation to manage the position of the nodes
            let tmpIndex = this.baseData.length;
            // flow deliveries are separated in 2 types, the main ones which are linked to the main dispatch (index 3) and the back up ones which are linked to the backup dispatch (index 4), the index is stored here for better understanding
            let mainIndex = this.baseData.length - 2;
            let backupIndex = this.baseData.length - 1;
            if (this.resourceBackUpMissing === true) {
                mainIndex = this.baseData.length - 1;
            }
            let takersItem = [];
            this.monitorData.details.flow_delivery_mains.forEach((delivery) => {
                data[mainIndex].next.push({index: tmpIndex, weight: 2});
                delivery.taker_outputs.forEach(taker => {
                    let savedTaker = takersItem.find(savedTaker => taker.taker_name === savedTaker.name);
                    let dataToPush = {
                        region: delivery.region,
                        redundancy_mode: taker.redundancy_mode,
                        redundancy_type: taker.redundancy_type,
                        configuration_uid: taker.configuration_uid,
                        source_protocol_type: taker.source_protocol_type,
                        main_receiver_name: taker.receiver_name
                    };
                    dataToPush["connected"] = taker.connected;
                    if (
                        this.userIsAtLeastAdmin
                        || this.userIsEntityAdmin(this.entityUid)
                    ){
                        dataToPush["zixi_conf"] = taker.zixi_conf;
                    }
                    if (savedTaker) {
                        savedTaker.parentIndex.push(tmpIndex);
                        savedTaker.taker_data.push(dataToPush);
                        if (savedTaker.status === 'success' || taker.status === 'CRITICAL') {
                            savedTaker.status = this.statusForPipeline[taker.status];
                        }
                        if(!dataToPush.connected && savedTaker.status === 'success'){
                            savedTaker.status = 'unstable';
                        }
                    } else {
                        let status = '';
                        if(!dataToPush.connected && taker.status === 'OK'){
                            status = 'unstable';
                        } else {
                            status = this.statusForPipeline[taker.status];
                        }
                        takersItem.push({
                            parentIndex: [tmpIndex],
                            taker_data: [dataToPush],
                            name: taker.taker_name,
                            hint: taker.taker_name,
                            status: status,
                            taker_status: taker.status,
                        });
                    }
                });
                tmpIndex++;
            });
            // 2023-O7-10 RPO: should be merge with main ? (only useful information is backup_receiver_name)
            if (this.monitorData.details.flow_delivery_backups !== null && this.monitorData.details.flow_delivery_backups !== undefined) {
                this.monitorData.details.flow_delivery_backups.forEach((delivery) => {
                    data[backupIndex].next.push({index: tmpIndex, weight: 2});
                    delivery.taker_outputs.forEach(taker => {
                        let savedTaker = takersItem.find(savedTaker => taker.taker_name === savedTaker.name);
                        let dataToPush = {
                            region: delivery.region,
                            backup_receiver_name: taker.receiver_name
                        };
                        dataToPush["connected"] = taker.connected;
                        if (
                            this.userIsAtLeastAdmin
                            || this.userIsEntityAdmin(this.entityUid)
                        ){
                            dataToPush["zixi_conf"] = taker.zixi_conf;
                        }
                        if (savedTaker) {
                            savedTaker.parentIndex.push(tmpIndex);
                            savedTaker.taker_data.push(dataToPush);
                            if (savedTaker.status === 'success' || taker.status === 'CRITICAL') {
                                savedTaker.status = this.statusForPipeline[taker.status];
                            }
                            if(!dataToPush.connected && savedTaker.status === 'success'){
                                savedTaker.status = 'unstable';
                            }
                        } else {
                            let status = '';
                            if(!dataToPush.connected && taker.status === 'OK'){
                                status = 'unstable';
                            } else {
                                status = this.statusForPipeline[taker.status];
                            }
                            takersItem.push({
                                parentIndex: [tmpIndex],
                                taker_data: [dataToPush],
                                name: taker.taker_name,
                                hint: taker.taker_name,
                                status: status,
                                taker_status: taker.status,
                            });
                        }
                    });
                    tmpIndex++;
                });
            }
            // here we link the preview node to the main dispatch node and to the backup dispatch node
            // the "next" key is the one used by the library to generated the arrows between the nodes
            // hiding the first link because it messes up the ordering when multiple parent have the same node in their `next` array

            if(this.resourceIsFeed){
                data[mainIndex].next.push({index: tmpIndex, weight: 2});
                if (this.resourceBackUpMissing === false) {
                    data[backupIndex].next.push({index: tmpIndex, weight: 2});
                }
                data.push(this.previewData);
                tmpIndex++;
            }

            takersItem.forEach(taker => {
                taker.parentIndex.forEach(parentIndex => {
                    data[parentIndex].next.push({index: tmpIndex, weight: 2});
                });
                data.push({
                    name: taker.name,
                    hint: taker.hint,
                    status: taker.status,
                    type: 'taker',
                    taker_data: taker.taker_data,
                    taker_status: this.statusForPipeline[taker.taker_status]
                });
                tmpIndex++;
            });

            //Used to display resource in right category on index.
            let hasError = false;
            data.map(item =>{
                if(item.taker_status && item.taker_status === 'CRITICAL'){
                    hasError = true;
                }else if(item.status === 'failure'){
                    hasError = true;
                }
            });
            if(hasError){
                this.$emit('setFailedResource', {resourceUid : this.resource.uid, isFeed:this.resourceIsFeed});
            }else{
                this.$emit('setRunningResource', {resourceUid : this.resource.uid, isFeed:this.resourceIsFeed});
            }
            return data;
        },
        globalClass(){
            if(this.resource.status === 'FAILED' || this.monitorData.global_status === undefined || this.monitorData.global_status === 'CRITICAL'){
                return 'el-icon-error text-danger is-big';
            } else if (this.monitorData.global_status === 'WARNING') {
                return 'el-icon-warning text-warning is-big';
            } else {
                return 'el-icon-success text-success is-big';
            }
        },
    },
    mounted(){
        let pendingReconf = null;
        let field = this.resourceIsFeed?'feed_uid':'channel_uid';
        if(
            this.reconfigurations.length > 0
            && (this.userIsAtLeastAdmin || this.userIsEntityAdmin(this.entityUid))
        ){
            pendingReconf = this.reconfigurations.find(reconf => reconf[field] === this.resource.uid);
        }
        if(pendingReconf){
            let data = {
                name: pendingReconf.node_name,
                action: pendingReconf.reconfiguration_type.toLowerCase(),
                url: pendingReconf.url,
            };
            this.nodeInfo = this.$refs.pipeline.nodeList.find(node => node.name === pendingReconf.node_name);

            if(!['reconfigure'].includes(data.action) && this.nodeInfo.type === 'taker'){
                data['isVM'] = true;
            }

            this.watchReconfiguration(data);
        }
    },
    beforeDestroy(){
        clearTimeout(this.reconfigurationTimeoutId);
    },
    methods: {
        modificationDelta() {
            let now = moment();
            let modificationDate = moment(this.monitorData.modification_date);
            return moment.duration(modificationDate.diff(now)).humanize(true);
        },
        handleSelect(node){
            if(node.collapse){
                this.collapsed = false;
            }
            this.nodeInfo = node;
        },
        setMainDeliveryName(delivery){
            if(this.resourceIsFeed || !this.resource.is_blankable){
                return `M. (${delivery.region})`;
            }
            return `M. ${delivery.profile_or_taker_name}`;
        },
        setBackupDeliveryName(delivery){
            if(this.resourceIsFeed || !this.resource.is_blankable){
                return `BU. (${delivery.region})`;
            }
            return `BU. ${delivery.profile_or_taker_name}`;
        },
        watchReconfiguration(data){
            if(!this.isWatchingStatus){
                this.setActionLabels(data.action);
                this.resourceName = data.name;
                this.requestUrl = data.url;
                this.isVM = data.isVM;
                this.loading = true;
                this.isWatchingStatus = true;
            }
            clearTimeout(this.reconfigurationTimeoutId);
            this.$store.dispatch('remoteGet', {
                url: this.requestUrl
            }).then(res => {

                if(['COMPLETED', 'FAILED'].includes(res.data.status)){
                    if(res.data.status === 'COMPLETED'){
                        this.$notify({
                            type: 'success',
                            title: `“${this.isVM?this.resourceName+'\'s blanking VM':this.resourceName}” was successfully ${this.actionLabels.verb}.`
                        });
                        this.resetWatchData();
                    }else{
                        this.loading = false;
                        this.isWatchingStatus = false;
                        this.canTryAgain = true;
                        this.$notify({
                            type: 'error',
                            title: `“${this.isVM?this.resourceName+'\'s blanking VM':this.resourceName}” could not be ${this.actionLabels.verb}.`
                        });
                    }
                } else {
                    this.reconfigurationTimeoutId = setTimeout(() => {
                        this.watchReconfiguration(this.requestUrl);
                    }, 3000);
                }
            });
        },
        setActionLabels(action){
            if(action === 'restart'){
                this.actionLabels = {
                    action: 'restart',
                    verb: 'restarted',
                    loading: 'Restarting',
                };

            }else if(action === 'reconfigure'){
                this.actionLabels = {
                    action: 'reconfigure',
                    verb: 'reconfigured',
                    loading: 'Reconfiguring',
                };
            }else{
                this.actionLabels = {
                    action: 'recreate',
                    verb: 'recreated',
                    loading: 'Recreating',
                };
            }
        },
        cancelRetry(){
            this.resetWatchData();
        },
        resetWatchData(){
            this.canTryAgain = false;
            this.loading = false;
            this.isWatchingStatus = false;
            this.requestUrl = '';
            this.resourceName= '';
        }
    }
};
</script>

<style lang="scss">
svg.pipeline{
    title, text.pipeline-node-label{
        color: #eeeeee;
        fill: #eeeeee;
    }
    .pipeline-node-end__text{
        fill: #eeeeee;
    }
}

.channel-details {
    .resource-container{
        position: relative;
        .overlay{
            background-color: #111;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 99;
            .loader-container{
                position: relative;
                top: 50%;
                transform: translate(-15px, -15px);
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                .loader{
                    transform: translate(-15px, -15px);
                }
                .loader-text{
                    font-family: 'Montserrat';
                    font-style: normal;
                    font-weight: 700;
                    font-size: 18px;
                    line-height: 150%;
                    margin-top: 1rem;
                }
            }
            .retry-container{
                position: relative;
                top: 50%;
                transform: translate(0, -50%);

                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;

                font-family: 'Montserrat';
                font-style: normal;
                font-weight: 400;
                font-size: 14px;
                line-height: 140%;

                .retry-title{
                    margin-top: 1rem;
                    font-style: bold;
                    font-weight: 700;
                    font-size: 18px;
                }
            }
        }
    }
    .node-details {
        max-height: 350px;
        max-width: calc(100% - 580px); // svg max size
        overflow-y: auto;
        flex-grow: 1;
        padding: 0 20px 20px;
        word-break: break-word;
    }
    .action-button{
        font-family: 'Montserrat';
        font-style: normal;
        font-weight: 500;
        font-size: 14px;
        line-height: 17px;
    }
    .cancel-button{
        background-color: #121212;
        border: solid 1px white;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/page/Monitoring/monitoring-resources.vue